import ServerSearchFilter from '@/components/CommonComponents/ServerSearchFilter';
import SiteInfoCard from '@/components/CommonComponents/SiteInfoCard';
import ViewDetailsModal from '@/components/CommonComponents/ViewDetailsModal';
import PageHeaderWrapper from '@/components/Layout/PageHeaderWrapper';
import { showToast } from '@/helper/toast.helper';
import { useDebounce } from '@/hooks/useDebounce';
import { useExportFile } from '@/hooks/useExportFile';
import { usePermission } from '@/hooks/usePermission';
import { sitesService } from '@/services/siteManagementService/siteManagementService';
import { AppDispatch, RootState } from '@/store';
import {
  fetchSitesList,
  toggleSiteStatus,
} from '@/store/slices/siteManagementSlice/siteManagementSlice';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

type StatusOption = {
  value: boolean;
  label: string;
} | null;

const pageSizes = [50, 75, 100];

// const getSiteDetailFields = (site: any) => {
//   if (!site) return [];

//   return [
//     { label: 'Site Name', value: site.name || 'N/A', half: true },

//     { label: 'Country', value: site.country?.name || 'N/A', half: true },

//     {
//       label: 'Longitude',
//       value: site.geofence?.centerLng !== undefined ? site.geofence.centerLng : 'N/A',
//       half: true,
//     },

//     {
//       label: 'Latitude',
//       value: site.geofence?.centerLat !== undefined ? site.geofence.centerLat : 'N/A',
//       half: true,
//     },

//     {
//       label: 'Radius',
//       value:
//         site.geofence?.radiusMeters !== undefined
//           ? `${(site.geofence.radiusMeters / 1000).toFixed(2)} km`
//           : 'N/A',
//       half: true,
//     },

//     { label: 'Max Users', value: site.maxUsers ?? 'N/A', half: true },

//     {
//       label: 'Language(s)',
//       value:
//         Array.isArray(site.languages) && site.languages.length > 0
//           ? site.languages.map((l: any) => l.name).join(', ')
//           : 'N/A',
//       colSpan: 2,
//     },

//     { label: 'Reward Currency', value: site.currency || 'N/A', half: true },

//     { label: 'Project', value: site.project?.name || 'N/A', half: true },

//     {
//       label: 'Status',
//       value: typeof site.status === 'boolean' ? (site.status ? 'Active' : 'Inactive') : 'N/A',
//       badge: true,
//       colSpan: 2,
//     },
//   ];
// };

const getSiteDetailFields = (site: any) => {
  if (!site) return [];

  return [
    { label: 'Site Name', value: site.name || 'N/A', half: true },

    { label: 'Country', value: site.country?.name || 'N/A', half: true },

    {
      label: 'Longitude',
      value: site.geofence?.centerLng !== undefined ? site.geofence.centerLng : 'N/A',
      half: true,
    },

    {
      label: 'Latitude',
      value: site.geofence?.centerLat !== undefined ? site.geofence.centerLat : 'N/A',
      half: true,
    },

    {
      label: 'Radius',
      value:
        site.geofence?.radiusMeters !== undefined
          ? `${(site.geofence.radiusMeters / 1000).toFixed(2)} km`
          : 'N/A',
      half: true,
    },

    { label: 'Max Users', value: site.maxUsers ?? 'N/A', half: true },

    {
      label: 'Language(s)',
      value:
        Array.isArray(site.languages) && site.languages.length > 0
          ? site.languages.map((l: any) => l.name).join(', ')
          : 'N/A',
      colSpan: 2,
    },

    { label: 'Reward Currency', value: site.currency || 'N/A', half: true },

    { label: 'Project', value: site.project?.name || 'N/A', half: true },

    {
      label: 'Status',
      value: typeof site.status === 'boolean' ? (site.status ? 'Active' : 'Inactive') : 'N/A',
      badge: true,
      colSpan: 2,
    },
  ];
};

export default function AllSitesPage() {
  const navigate = useNavigate();
  const { exportFile } = useExportFile();
  const dispatch = useDispatch<AppDispatch>();
  const lastRequestRef = useRef('');

  const { list, loading, sitesStats } = useSelector((state: RootState) => state.sites);

  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const [openSiteModal, setOpenSiteModal] = useState(false);
  const [selectedSite, setSelectedSite] = useState<any>(null);
  const [open, setOpen] = useState(false);

  const [filters, setFilters] = useState<{
    search?: string;
    status?: boolean;
  }>({});

  const debouncedFilters = useDebounce(filters, 500);
  const sitesData = list?.data || [];
  const totalPages = list?.totalPages || 1;
  const total = list?.total || 0;
  const loadedCount = sitesData.length;

  const remainingCount = Math.max(total - loadedCount, 0);
  const hasMore = currentPage < totalPages;

  const permissions = {
    create: usePermission('create', 'sites'),
    edit: usePermission('edit', 'sites'),
    export: usePermission('export', 'sites'),
  };

  useEffect(() => {
    const reqKey = `${currentPage}-${pageSize}-${filters.search}-${filters.status}`;
    if (reqKey === lastRequestRef.current) return;

    lastRequestRef.current = reqKey;

    dispatch(
      fetchSitesList({
        page: currentPage,
        limit: pageSize,
        search: debouncedFilters?.search,
        status: debouncedFilters?.status,
      }),
    );
  }, [currentPage, pageSize, debouncedFilters, dispatch]);

  const handleExport = () => {
    exportFile(() => sitesService.exportSites(), `sites_${Date.now()}.csv`);
  };

  const handleViewSite = useCallback((site: any) => {
    setSelectedSite(site);
    setOpenSiteModal(true);
  }, []);

  const loadMore = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
    }
  };

  const handleSeeLess = () => {
    setCurrentPage(1);
  };

  const handleFilterChange = (filters: Record<string, any>) => {
    if (filters?.pageSize) {
      setPageSize(Number(filters.pageSize));
      setCurrentPage(1);
    }

    let statusValue: boolean | undefined;

    if (filters.status === 'Active') {
      statusValue = true;
    } else if (filters.status === 'Inactive') {
      statusValue = false;
    } else {
      statusValue = undefined;
    }

    setFilters({
      search: filters?.search || undefined,
      status: statusValue,
    });

    setCurrentPage(1);
  };

  const handleToggleStatus = useCallback(async (site: any) => {
    try {
      const newStatus = !site.status;

      await dispatch(
        toggleSiteStatus({
          id: site.id,
          status: newStatus,
        }),
      ).unwrap();

      if (!newStatus) {
        showToast.warning('Site unarchived successfully.');
      } else {
        showToast.success('Site archived successfully.');
      }
      dispatch(
        fetchSitesList({
          page: currentPage,
          limit: pageSize,
          search: debouncedFilters?.search,
          status: debouncedFilters?.status,
        }),
      );
    } catch (error: any) {
      showToast.error(error?.message || 'Failed to update site status');
    }
  }, []);

  const handleSiteArrowClick = (site: any) => {
    navigate('/dashboard/site-management/EditSite', {
      state: {
        projectId: site.project?.id,
        siteId: site.id,
        from: 'site-management',
      },
    });
  };

  return (
    <PageHeaderWrapper
      title="Site Management"
      description="View and monitor all your sites."
      exportLabel="Export"
      onExport={handleExport}
      addLabel="Return to Site Dashboard"
      onAdd={() => navigate(-1)}
      permissions={permissions}
    >
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div className="w-full ">
          <ServerSearchFilter
            fields={[
              {
                type: 'search',
                key: 'search',
                placeholder: 'Search sites...',
              },
              {
                type: 'radio-select',
                key: 'status',
                label: 'Status',
                options: ['Active', 'Inactive'],
              },
              {
                type: 'radio-select',
                key: 'pageSize',
                label: 'limitSites',
                options: pageSizes.map((size) => size.toString()),
              },
            ]}
            onChange={handleFilterChange}
          />
        </div>
      </div>

      {/* Site Cards */}
      <div className="">
        {/* No sites message */}
        {!loading && sitesData.length === 0 && (
          <div className="text-center text-gray-500 py-10">No sites found</div>
        )}
        {/* Grid of Sites */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {sitesData.map((site: any) => (
            <SiteInfoCard
              key={site.id}
              site={site}
              onView={handleViewSite}
              handleArrowClick={handleSiteArrowClick}
              onToggleStatus={handleToggleStatus}
              permissions={permissions}
            />
          ))}

          {/* Load More Card */}
          {hasMore && remainingCount > 0 && (
            <button
              onClick={loadMore}
              className="relative flex flex-col justify-between bg-[#AFAFAF] text-[#2B1C25] rounded-lg shadow hover:bg-[#9e8895] transition-all p-5 w-full"
              disabled={loading}
            >
              <div className="flex flex-col items-start">
                <span className="text-2xl font-bold">{loading ? 'Loading...' : 'Load More'}</span>
                {!loading && (
                  <span className="text-sm">
                    {remainingCount} site
                    {remainingCount !== 1 ? 's' : ''} remaining
                  </span>
                )}
              </div>
              <ArrowDown
                className="absolute bottom-4 right-4 bg-transparent cursor-pointer p-1 rounded text-[#2B1C25]"
                height={44}
                width={44}
              />
            </button>
          )}

          {/* See less Card */}
          {currentPage > 1 && (
            <button
              onClick={handleSeeLess}
              className="relative flex flex-col justify-between bg-[#AFAFAF] text-[#2B1C25] rounded-lg shadow hover:bg-[#9e8895] transition-all p-5 w-full"
            >
              <div className="flex flex-col items-start">
                <span className="text-2xl font-bold">See Less</span>
              </div>
              <ArrowUp
                className="absolute bottom-4 right-4 bg-transparent cursor-pointer p-1 rounded text-[#2B1C25]"
                height={44}
                width={44}
              />
            </button>
          )}
        </div>
      </div>

      <ViewDetailsModal
        isOpen={openSiteModal}
        onClose={() => setOpenSiteModal(false)}
        title="Site Details"
        data={selectedSite || {}}
        fields={getSiteDetailFields(selectedSite)}
      />
    </PageHeaderWrapper>
  );
}
